import { apiClient } from '@/lib/apiClient';

export interface EmailTemplate {
  id: string;
  name: string;
  description?: string;
  category?: string;
  subject: string;
  body: string;
  body_html?: string;
  is_active?: boolean;
  /** Shared library template — read-only for regular users; superusers can edit/delete. */
  is_global?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface EmailTemplateInput {
  name: string;
  description?: string;
  category?: string;
  subject: string;
  body: string;
  body_html?: string;
  is_active?: boolean;
}

export interface EmailTemplateListResponse {
  success: boolean;
  templates?: EmailTemplate[];
  /** Legacy response shape. */
  items?: EmailTemplate[];
  total?: number;
  limit?: number;
  offset?: number;
  message?: string;
}

export interface EmailTemplateResponse {
  success: boolean;
  template?: EmailTemplate;
  message?: string;
}

export interface RenderTemplatePayload {
  contact_id?: string | number;
  lead_id?: string | number;
}

export interface RenderTemplateResponse {
  success: boolean;
  subject?: string;
  body?: string;
  body_html?: string;
  recipient?: {
    email?: string;
    name?: string;
  };
  message?: string;
}

/** Coerce MySQL-style truthy values (1, "1", "true") to real booleans. */
const toBool = (v: unknown): boolean => v === true || v === 1 || v === '1' || v === 'true';

const normalizeTemplate = (t: EmailTemplate): EmailTemplate => ({
  ...t,
  is_global: toBool(t.is_global),
  is_active: t.is_active === undefined ? undefined : toBool(t.is_active),
});

/**
 * Fetch all email templates for the current user.
 */
export const fetchEmailTemplates = async (
  category?: string,
  search?: string,
  active?: boolean
): Promise<EmailTemplate[]> => {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (search) params.set('q', search);
  if (active !== undefined) params.set('active', String(active));
  const query = params.toString();
  const response = await apiClient.get<EmailTemplateListResponse>(
    `/email/templates${query ? `?${query}` : ''}`
  );
  return (response.templates || response.items || []).map(normalizeTemplate);
};

/**
 * Fetch a single email template by ID.
 */
export const fetchEmailTemplate = async (id: string): Promise<EmailTemplate> => {
  const response = await apiClient.get<EmailTemplateResponse>(`/email/templates/${id}`);
  if (!response.template) {
    throw new Error('Template not found');
  }
  return normalizeTemplate(response.template);
};

/**
 * Create a new email template.
 * Backend may reply with either the full template or just `{ success, id }`.
 */
export const createEmailTemplate = async (
  data: EmailTemplateInput
): Promise<EmailTemplate> => {
  const response = await apiClient.post<EmailTemplateResponse & { id?: string }>(
    '/email/templates',
    data
  );
  if (response.template) return normalizeTemplate(response.template);
  if (response.success) {
    return normalizeTemplate({ id: response.id || '', ...data } as EmailTemplate);
  }
  throw new Error(response.message || 'Failed to create template');
};

/**
 * Update an existing email template.
 * Backend may reply with either the full template or just `{ success, updated }`.
 */
export const updateEmailTemplate = async (
  id: string,
  data: Partial<EmailTemplateInput>
): Promise<EmailTemplate> => {
  const response = await apiClient.put<EmailTemplateResponse & { updated?: number }>(
    `/email/templates/${id}`,
    data
  );
  if (response.template) return normalizeTemplate(response.template);
  if (response.success) {
    return normalizeTemplate({ id, ...data } as EmailTemplate);
  }
  throw new Error(response.message || 'Failed to update template');
};


/**
 * Delete an email template.
 */
export const deleteEmailTemplate = async (id: string): Promise<void> => {
  await apiClient.delete<EmailTemplateResponse>(`/email/templates/${id}`);
};

/**
 * Render a template against a contact/lead, returning the filled subject and body.
 */
export const renderEmailTemplate = async (
  id: string,
  payload: RenderTemplatePayload
): Promise<RenderTemplateResponse> => {
  return await apiClient.post<RenderTemplateResponse>(`/email/templates/${id}/render`, payload);
};

export interface TemplatePlaceholder {
  field: string;
  tokens?: string[];
  description?: string;
  /** 'recipient' = lead/contact data, 'sender' = authenticated user data. */
  source?: 'recipient' | 'sender' | string;
}

export interface TemplatePlaceholdersResponse {
  success: boolean;
  placeholders?: TemplatePlaceholder[];
  message?: string;
}

/**
 * Fetch the placeholder catalog supported by the render endpoint.
 */
export const fetchTemplatePlaceholders = async (): Promise<TemplatePlaceholder[]> => {
  const response = await apiClient.get<TemplatePlaceholdersResponse>('/email/templates/placeholders');
  return response.placeholders || [];
};
